import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaTrashAlt, FaEdit, FaPlus, FaCoins, FaAd, FaUndo, FaStar, FaCalendarCheck, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import Calling from '../../../api/Calling';
import Loader from '../../../components/Loader';
import Navbar from '../../Quiz/Navbar';
import generateCaptcha from '../../../components/captcha';


const DailyWatchAd = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [globalStatus, setGlobalStatus] = useState(true);
    const [adEnabled, setAdEnabled] = useState(false); // New state for ad toggle
    const [formData, setFormData] = useState({
        daily_coin: '',
        ad_coin: '0' // Default to 0 for ad_coin
    });
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [totals, setTotals] = useState({
        totalDailyCoins: 0,
        totalAdCoins: 0,
        totalCombinedReward: 0
    });
    const [fieldErrors, setFieldErrors] = useState({});


    // Calculate totals from data
    const calculateTotals = (dataArray) => {
        if (!dataArray || dataArray.length === 0) return;

        const totalDailyCoins = dataArray.reduce((sum, item) => sum + (item.daily_coin || 0), 0);
        const totalAdCoins = dataArray.reduce((sum, item) => sum + (item.ad_coin || 0), 0);
        const totalCombinedReward = totalDailyCoins + totalAdCoins;

        setTotals({
            totalDailyCoins,
            totalAdCoins,
            totalCombinedReward
        });
    };

    // Fetch data
    const fetchData = async () => {
        try {
            setLoader(true);
            const resp = await Calling?.getDailyWatchAdsEarn();
            if (resp?.status) {
                const fetchedData = resp?.data || [];
                setData(fetchedData);
                calculateTotals(fetchedData);
                // Set global status from response
                setGlobalStatus(resp?.daily_ads_watch_earn_status !== undefined ?
                    resp.daily_ads_watch_earn_status : true);
            } else {
                toast.error(resp?.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.log(error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle item selection
    const handleSelect = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(item => item !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    // Handle multiple delete
    const handleMultipleDelete = async () => {
        const content = `You are about to delete daily reward data. To confirm this action, please solve the math problem below.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                setLoader(true);
                const resp = await Calling.deleteDailyWatchAdsEarn({ ids: selectedItems });
                if (resp?.status) {
                    toast.success(resp?.message || 'Items deleted successfully');
                    setSelectedItems([]);
                    fetchData(); // Fetch data after successful deletion
                } else {
                    toast.error(resp?.message || 'Failed to delete items');
                }
            } catch (error) {
                console.log(error);
                toast.error('Something went wrong');
            } finally {
                setLoader(false);
            }
        }
    };

    // Handle single delete
    const handleDelete = async (id) => {
        const content = `You are about to delete daily reward data. To confirm this action, please solve the math problem below.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                setLoader(true);
                const resp = await Calling.deleteDailyWatchAdsEarn({ ids: [id] });
                if (resp?.status) {
                    toast.success(resp?.message || 'Item deleted successfully');
                    fetchData(); // Fetch data after successful deletion
                } else {
                    toast.error(resp?.message || 'Failed to delete item');
                }
            } catch (error) {
                console.log(error);
                toast.error('Something went wrong');
            } finally {
                setLoader(false);
            }
        } else {
            toast.error('Answer is not correct', { autoClose: 2000 });
        }
    };

    // Handle edit
    const handleEdit = (item) => {
        setFormData({
            daily_coin: item.daily_coin,
            ad_coin: item.ad_coin
        });
        setEditMode(true);
        setEditId(item._id);
        setIsModalOpen(true);
        setFieldErrors({});
        // Set ad enabled based on ad_coin value
        setAdEnabled(item.ad_coin > 0);
    };

    // Handle form change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFieldErrors({
            ...fieldErrors,
            [name]: '' // Clear error on change
        });

        // Handle numeric fields
        if (name === 'daily_coin' || name === 'ad_coin') {
            setFormData({
                ...formData,
                [name]: value === '' ? '0' : parseInt(value, 10) || 0
            });
            return;
        }

        // Handle other fields if needed
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Toggle ad enabled status
    const toggleAdEnabled = () => {
        const newAdEnabled = !adEnabled;
        setAdEnabled(newAdEnabled);

        // If ads are disabled, set ad_coin to 0
        if (!newAdEnabled) {
            setFormData({
                ...formData,
                ad_coin: '0'
            });
        }
    };

    // Enhanced validation for form submission
    const validateForm = () => {
        const errors = {};

        if (formData.daily_coin === '' || formData.daily_coin === null) {
            errors.daily_coin = 'Daily coin reward is required';
        } else if (parseInt(formData.daily_coin, 10) < 1) {
            errors.daily_coin = 'Daily coin reward must be at least 1';
        }

        // Allow ad_coin to be 0
        if (formData.ad_coin === '' || formData.ad_coin === null) {
            // Just set a default value rather than showing an error
            formData.ad_coin = 0;
        }

        setFieldErrors(errors);
        return errors;
    };

    // Handle form submit with enhanced validation
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            for (const error of Object.values(errors)) {
                toast.error(error);
            }
            return;
        }

        try {
            setLoader(true);
            let resp;

            // Ensure daily_coin is at least 1 and ad_coin is at least 0
            const submissionData = {
                ...formData,
                daily_coin: Math.max(1, parseInt(formData.daily_coin, 10) || 1),
                ad_coin: adEnabled ? (parseInt(formData.ad_coin, 10) || 0) : 0
            };

            if (editMode) {
                resp = await Calling.updateDailyWatchAdsEarn(editId, submissionData);
            } else {
                resp = await Calling.insertDailyWatchAdsEarn(submissionData);
            }

            if (resp?.status) {
                toast.success(resp?.message || `Successfully ${editMode ? 'updated' : 'added'}`);
                fetchData();
                setIsModalOpen(false);
                resetForm();
            } else {
                toast.error(resp?.message || 'Operation failed');
            }
        } catch (error) {
            console.log(error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            daily_coin: '',
            ad_coin: '0' // Default to 0
        });
        setEditMode(false);
        setEditId(null);
        setFieldErrors({});
        setAdEnabled(false); // Reset ad enabled state
    };

    // Stats card component
    const StatCard = ({ icon, title, value, color, subtitle }) => (
        <div className={`bg-white p-4 rounded-xl shadow-md border-l-4 ${color}`}>
            <div className="flex items-center">
                <div className={`p-3 rounded-full ${color.replace('border-', 'bg-').replace('-main-blue', '-main-blue/10').replace('-main-yellow', '-main-yellow/10')} mr-4`}>
                    {icon}
                </div>
                <div>
                    <p className="text-sm text-main-subfont">{title}</p>
                    <p className="text-xl font-bold text-main-font">{value}</p>
                    {subtitle && <p className="text-xs text-main-subfont mt-1">{subtitle}</p>}
                </div>
            </div>
        </div>
    );

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Daily Streak</h1>
            <div className='bg-main-white rounded-t-xl shadow-lg'>
                <Navbar selectState={'streak'} />
                <hr className='border-gray-200' />
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                setIsModalOpen(true);
                                resetForm();
                            }}
                            className="flex items-center gap-2 bg-main-blue hover:bg-main-blue/90 text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-md"
                        >
                            <FaPlus /> Add New Reward
                        </button>
                        {selectedItems.length > 0 && (
                            <button
                                onClick={handleMultipleDelete}
                                className="flex items-center gap-2 bg-main-delete hover:bg-main-delete/90 text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-md"
                            >
                                <FaTrashAlt />
                                Delete ({selectedItems.length})
                            </button>
                        )}
                    </div>

                    {/* Global Status Toggle */}
                    <div className="mt-4 sm:mt-0 flex items-center gap-2">
                        <div
                            className={`flex items-center gap-2 text-sm px-4 py-1.5 rounded-full shadow-sm border
                  ${globalStatus
                                    ? 'bg-green-100 text-green-600 border-green-300'
                                    : 'bg-red-100 text-red-600 border-red-300'
                                }`}
                        >
                            <span
                                className={`h-2 w-2 rounded-full
                  ${globalStatus ? 'bg-green-500' : 'bg-red-500'}
                  `}
                            ></span>
                            {globalStatus ? 'Active' : 'Inactive'}
                        </div>
                    </div>
                </div>

                {data.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mx-4 mb-6">
                        <StatCard
                            icon={<FaCoins className="text-main-yellow text-xl" />}
                            title="Total Daily Streak Coins"
                            value={totals.totalDailyCoins}
                            subtitle={`Across ${data.length} reward${data.length > 1 ? 's' : ''}`}
                            color="border-main-yellow"
                        />
                        <StatCard
                            icon={<FaAd className="text-main-blue text-xl" />}
                            title="Total Ad Watch Coins"
                            value={totals.totalAdCoins}
                            subtitle={`Across ${data.length} reward${data.length > 1 ? 's' : ''}`}
                            color="border-main-blue"
                        />
                        <StatCard
                            icon={<FaCalendarCheck className="text-main-blue text-xl" />}
                            title="Total Combined Reward"
                            value={totals.totalCombinedReward}
                            subtitle={`Daily streak + ad coins`}
                            color="border-main-blue"
                        />
                    </div>
                )}

                {loader ? (
                    <div className="py-8">
                        <Loader loader={loader} />
                    </div>
                ) : (
                    <>
                        {data.length === 0 ? (
                            <div className="p-8 text-center">
                                <div className="text-9xl text-gray-200 flex justify-center mb-4">
                                    <FaStar />
                                </div>
                                <div className="text-gray-500 mb-4 text-lg">No reward data available yet</div>
                                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                                    Add your first daily streak reward to motivate users to return every day
                                </p>
                                <button
                                    onClick={() => {
                                        setIsModalOpen(true);
                                        resetForm();
                                    }}
                                    className="inline-flex items-center gap-2 bg-main-blue hover:bg-main-blue/80 text-white px-6 py-2 rounded-lg transition-colors duration-200 shadow-md"
                                >
                                    <FaPlus /> Add First Reward
                                </button>
                            </div>
                        ) : (
                            <div className="overflow-x-auto p-4">
                                <table className="min-w-full bg-white rounded-xl overflow-hidden">
                                    <thead>
                                        <tr className="bg-main-th text-main-subfont uppercase text-xs leading-normal">
                                            <th className="py-3 px-2 text-left w-16">
                                                <div className="flex items-center justify-center">
                                                    <input
                                                        type="checkbox"
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedItems(data.map(item => item._id));
                                                            } else {
                                                                setSelectedItems([]);
                                                            }
                                                        }}
                                                        checked={data.length > 0 && selectedItems.length === data.length}
                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                    />
                                                </div>
                                            </th>
                                            <th className="py-3 px-6 text-left">Daily Coins</th>
                                            <th className="py-3 px-6 text-left">Ad Coins</th>
                                            <th className="py-3 px-6 text-left">Combined Total</th>
                                            <th className="py-3 px-6 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 text-sm">
                                        {data.map((item) => (
                                            <tr key={item._id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                                                <td className="py-3 px-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.includes(item._id)}
                                                        onChange={() => handleSelect(item._id)}
                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                    />
                                                </td>
                                                <td className="py-3 px-6 text-left">
                                                    <div className="flex items-center">
                                                        <div className="p-2 bg-main-yellow/10 rounded-full mr-2">
                                                            <FaCoins className="text-main-yellow" />
                                                        </div>
                                                        <span className="font-medium">{item.daily_coin}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-6 text-left">
                                                    <div className="flex items-center">
                                                        <div className="p-2 bg-main-blue/10 rounded-full mr-2">
                                                            <FaAd className="text-main-blue" />
                                                        </div>
                                                        <span className="font-medium">{item.ad_coin}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-6 text-left">
                                                    <div className="flex items-center">
                                                        <div className="p-2 bg-main-blue/10 rounded-full mr-2">
                                                            <FaCalendarCheck className="text-main-blue" />
                                                        </div>
                                                        <span className="font-medium">{item.daily_coin + item.ad_coin}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-6 text-center">
                                                    <div className="flex item-center justify-center gap-2">
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="bg-main-blue/10 hover:bg-main-blue/20 p-2 rounded-full transform hover:scale-110 transition-all duration-200"
                                                            title="Edit"
                                                        >
                                                            <FaEdit className="text-main-blue" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item._id)}
                                                            className="bg-main-delete/10 hover:bg-main-delete/20 p-2 rounded-full transform hover:scale-110 transition-all duration-200"
                                                            title="Delete"
                                                        >
                                                            <FaTrashAlt className="text-main-delete" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal for Add/Edit with Ad Toggle */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md animate-fadeIn">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {editMode ? 'Edit Daily Streak Reward' : 'Add Daily Streak Reward'}
                            </h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-500 text-xl bg-gray-100 hover:bg-gray-200 rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200"
                            >
                                &times;
                            </button>
                        </div>
                        <div>
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="daily_coin">
                                    Daily Streak Coins <span className="text-main-delete">*</span>
                                </label>
                                <div className="flex items-center relative">
                                    <div className="absolute left-3">
                                        <FaCoins className="text-main-yellow" />
                                    </div>
                                    <input
                                        type="number"
                                        id="daily_coin"
                                        name="daily_coin"
                                        value={formData.daily_coin}
                                        onChange={handleChange}
                                        className={`shadow appearance-none border ${fieldErrors.daily_coin ? 'border-main-delete' : 'border-gray-300'} rounded w-full py-3 pl-10 pr-3 text-main-font leading-tight focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-transparent transition-all duration-200`}
                                        placeholder="Enter daily coin amount (minimum 1)"
                                        min="1"
                                        required
                                    />
                                </div>
                                {fieldErrors.daily_coin && (
                                    <p className="text-xs text-main-delete mt-1">{fieldErrors.daily_coin}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Users will earn this amount for daily app visits (minimum 1 coin required)</p>
                            </div>

                            {/* Ad Toggle Section */}
                            <div className="flex items-center justify-between p-5 border border-gray-200 rounded-xl bg-gray-50 mb-6">
                                <div>
                                    <span className="flex items-center gap-3 text-gray-700">
                                        <FaAd className="text-main-blue" size={20} />
                                        Ad Watch Rewards Enabled
                                    </span>
                                    <span className="text-xs text-gray-500 block mt-1">Allow users to earn additional coins by watching ads</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={adEnabled}
                                        onChange={toggleAdEnabled}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-main-blue"></div>
                                </label>
                            </div>

                            {/* Ad Coin Input - Only shown when adEnabled is true */}
                            {adEnabled && (
                                <div className="mb-6">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ad_coin">
                                        Ad Watch Coins
                                    </label>
                                    <div className="flex items-center relative">
                                        <div className="absolute left-3">
                                            <FaAd className="text-main-blue" />
                                        </div>
                                        <input
                                            type="number"
                                            id="ad_coin"
                                            name="ad_coin"
                                            value={formData.ad_coin}
                                            onChange={handleChange}
                                            className="shadow appearance-none border rounded w-full py-3 pl-10 pr-3 text-main-font leading-tight focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-transparent transition-all duration-200"
                                            placeholder="Enter ad coin amount"
                                            min="1"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Users will earn this amount for each ad watched</p>
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-5 rounded-lg focus:outline-none transition-colors duration-200 flex items-center gap-1"
                                >
                                    <FaUndo className="text-gray-600" /> Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className="bg-main-blue hover:bg-main-blue/80 text-white font-medium py-2 px-5 rounded-lg focus:outline-none transition-colors duration-200 flex items-center gap-1"
                                >
                                    {editMode ? <FaEdit /> : <FaPlus />}
                                    {editMode ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyWatchAd;